import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface OrganizationStats {
  totalMembers: number;
  activeRoles: number;
  departments: number;
  pendingApplications: number;
  completedQuotes: number;
  averageResponseTime: number;
}

export const useOrganizationStats = (providerId: string) => {
  const [stats, setStats] = useState<OrganizationStats>({
    totalMembers: 0,
    activeRoles: 0,
    departments: 0,
    pendingApplications: 0,
    completedQuotes: 0,
    averageResponseTime: 0
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchStats = async () => {
      if (!providerId) {
        console.log('useOrganizationStats: No providerId provided');
        setIsLoading(false);
        return;
      }

      try {
        console.log('useOrganizationStats: Fetching stats for providerId:', providerId);
        setIsLoading(true);
        setError(null);

        // Fetch total members (peo_representatives)
        const { data: members, error: membersError } = await supabase
          .from('peo_representatives')
          .select('id, department')
          .eq('provider_id', providerId)
          .eq('verification_status', 'verified');

        if (membersError) throw membersError;

        // Fetch active roles
        const { data: roles, error: rolesError } = await supabase
          .from('peo_organizational_roles')
          .select('id')
          .eq('provider_id', providerId);

        if (rolesError) throw rolesError;

        // Fetch pending verifications for this provider
        const { data: pendingVerifications, error: pendingError } = await supabase
          .from('peo_representatives')
          .select('id')
          .eq('provider_id', providerId)
          .eq('verification_status', 'pending');

        if (pendingError) throw pendingError;

        // Fetch quotes for this provider
        const { data: quotes, error: quotesError } = await supabase
          .from('peo_quotes')
          .select('id, status, created_at, updated_at')
          .eq('provider_id', providerId);

        if (quotesError) throw quotesError;

        // Calculate departments (unique departments from members)
        const uniqueDepartments = new Set(
          members?.filter(m => m.department).map(m => m.department) || []
        );

        // Calculate pending applications (now pending verifications)
        const pendingApplications = pendingVerifications?.length || 0;

        // Calculate completed quotes (using correct status values)
        const completedQuotes = quotes?.filter(q => 
          q.status === 'accepted' || q.status === 'declined'
        ).length || 0;

        // Calculate average response time (in hours)
        const responseTimes = quotes
          ?.filter(q => q.updated_at && q.created_at)
          .map(q => {
            const created = new Date(q.created_at).getTime();
            const updated = new Date(q.updated_at).getTime();
            return (updated - created) / (1000 * 60 * 60); // Convert to hours
          }) || [];

        const averageResponseTime = responseTimes.length > 0
          ? responseTimes.reduce((sum, time) => sum + time, 0) / responseTimes.length
          : 0;

        const newStats = {
          totalMembers: members?.length || 0,
          activeRoles: roles?.length || 0,
          departments: uniqueDepartments.size,
          pendingApplications,
          completedQuotes,
          averageResponseTime: Math.round(averageResponseTime)
        };
        
        console.log('useOrganizationStats: Fetched stats:', newStats);
        setStats(newStats);

      } catch (err) {
        console.error('Error fetching organization stats:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch stats');
      } finally {
        setIsLoading(false);
      }
    };

    fetchStats();
  }, [providerId]);

  return { stats, isLoading, error };
};